import React, { useContext, useEffect, useState } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
} from "@mui/material";
import { UserContext } from "../../../../App";
import {
  EventMetaContext,
  MedPlanContext,
  OpsPlanContext,
  RiskAssessmentContext,
} from "../SingleEvent";
import moment from "moment";
import API from "../../../../services/API";

const COLORS = {
  navy: "#163A5F",
  blue: "#268EBB",
  red: "#C94C4C",
  green: "#1F8A5B",
  purple: "#7A3E8E",
  orange: "#D9822B",
  ink: "#1F2937",
  text: "#334155",
  muted: "#64748B",
  line: "#D9E2EC",
  soft: "#F8FAFC",
  softBlue: "#F4F9FC",
  white: "#FFFFFF",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 72,
    paddingBottom: 56,
    paddingHorizontal: 32,
    fontFamily: "Helvetica",
    fontSize: 10,
    color: COLORS.text,
    backgroundColor: COLORS.white,
  },

  coverPage: {
    padding: 40,
    fontFamily: "Helvetica",
    color: COLORS.text,
    backgroundColor: COLORS.white,
  },

  topBar: {
    position: "absolute",
    top: 18,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  topBarLogo: {
    height: 42,
    maxWidth: 160,
    objectFit: "contain",
  },

  topBarOrg: {
    fontSize: 11,
    color: COLORS.muted,
  },

  footerLeft: {
    position: "absolute",
    bottom: 20,
    left: 32,
    fontSize: 10,
    color: COLORS.muted,
  },

  footerRight: {
    position: "absolute",
    bottom: 20,
    right: 32,
    fontSize: 10,
    color: COLORS.muted,
    textAlign: "right",
  },

  coverWrap: {
    flexDirection: "column",
    alignItems: "center",
    marginTop: 70,
  },

  coverKicker: {
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: COLORS.blue,
    marginBottom: 10,
  },

  coverTitle: {
    fontSize: 28,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 14,
    textAlign: "center",
  },

  coverEventTitle: {
    fontSize: 22,
    fontFamily: "Helvetica-Bold",
    color: COLORS.red,
    marginTop: 18,
    marginBottom: 8,
    textAlign: "center",
  },

  coverMeta: {
    fontSize: 12,
    color: COLORS.muted,
    textAlign: "center",
    marginBottom: 4,
  },

  coverLogo: {
    height: 120,
    maxWidth: 240,
    objectFit: "contain",
    marginVertical: 18,
  },

  coverCard: {
    marginTop: 28,
    border: "1pt solid #D9E2EC",
    borderRadius: 10,
    padding: 16,
    backgroundColor: COLORS.soft,
    width: "100%",
  },

  coverCardTitle: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    marginBottom: 8,
  },

  bodyTitle: {
    fontSize: 22,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
    textAlign: "center",
    marginBottom: 4,
  },

  bodySubtitle: {
    fontSize: 15,
    color: COLORS.red,
    textAlign: "center",
    marginBottom: 22,
  },

  sectionCard: {
    border: "1pt solid #D9E2EC",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 16,
    backgroundColor: COLORS.white,
  },

  sectionAccent: {
    height: 5,
    width: "100%",
  },

  sectionHeader: {
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 8,
    backgroundColor: COLORS.soft,
    borderBottom: "1pt solid #E5EAF0",
  },

  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: COLORS.ink,
  },

  sectionBody: {
    paddingHorizontal: 14,
    paddingVertical: 8,
  },

  infoRow: {
    paddingVertical: 8,
    borderBottom: "1pt solid #EEF2F6",
  },

  infoRowLast: {
    borderBottom: 0,
  },

  infoLabel: {
    fontSize: 8,
    textTransform: "uppercase",
    color: COLORS.muted,
    fontFamily: "Helvetica-Bold",
    letterSpacing: 0.5,
    marginBottom: 3,
  },

  infoValue: {
    fontSize: 10,
    color: COLORS.text,
    lineHeight: 1.55,
  },

  stackedText: {
    fontSize: 10,
    color: COLORS.text,
    lineHeight: 1.45,
    marginBottom: 2,
  },

  draftBanner: {
    backgroundColor: COLORS.red,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginBottom: 12,
  },

  draftBannerText: {
    textAlign: "center",
    color: COLORS.white,
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },

  table: {
    marginTop: 4,
    border: "1pt solid #D9E2EC",
    borderRadius: 8,
    overflow: "hidden",
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: COLORS.softBlue,
    borderBottom: "1pt solid #D9E2EC",
  },

  tableHeaderCell: {
    padding: 6,
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLORS.navy,
  },

  tableRow: {
    flexDirection: "row",
    borderBottom: "1pt solid #EEF2F6",
  },

  tableCell: {
    padding: 6,
    fontSize: 9,
    color: COLORS.text,
    lineHeight: 1.45,
  },

  destinationCard: {
    border: "1pt solid #D9E2EC",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    backgroundColor: "#F8FBFD",
  },

  destinationType: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#5B6B7A",
    marginBottom: 4,
    textTransform: "uppercase",
  },

  destinationName: {
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    color: "#1F2D3D",
    marginBottom: 8,
  },

  destinationAccessBlock: {
    marginTop: 10,
    paddingTop: 8,
    borderTop: "1pt solid #E5EAF0",
    width: "100%",
  },

  destinationMetaRow: {
    flexDirection: "row",
  },

  destinationMetaBlock: {
    flexGrow: 1,
    flexBasis: 0,
    paddingRight: 10,
  },

  destinationMetaLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
    color: "#7B8794",
    marginBottom: 3,
    textTransform: "uppercase",
  },

  destinationMetaText: {
    fontSize: 9,
    color: "#243B53",
    marginBottom: 2,
    lineHeight: 1.4,
  },

  confidentialText: {
    fontSize: 9,
    color: "#C81E1E",
    fontFamily: "Helvetica-Bold",
    marginTop: 2,
  },
});

const SectionCard = ({ headerName, color, rows }) => {
  const filteredRows = rows.filter(
    (row) => row.value != null && row.value !== "",
  );

  if (!filteredRows.length) return null;

  return (
    <View style={styles.sectionCard}>
      <View style={[styles.sectionAccent, { backgroundColor: color }]} />
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>{headerName}</Text>
      </View>

      <View style={styles.sectionBody}>
        {filteredRows.map((row, index) => {
          const isLast = index === filteredRows.length - 1;

          return (
            <View
              key={row.title}
              style={[styles.infoRow, isLast && styles.infoRowLast]}
              minPresenceAhead={36}
            >
              <Text style={styles.infoLabel}>{row.title}</Text>

              {Array.isArray(row.value) ? (
                row.value.map((string, idx) => (
                  <Text key={idx} style={styles.stackedText}>
                    {string}
                  </Text>
                ))
              ) : typeof row.value === "string" ? (
                <Text style={styles.infoValue}>{row.value}</Text>
              ) : (
                row.value
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

const Destination = ({
  type,
  name,
  contactLines = [],
  addressLines = [],
  accessInfo,
}) => {
  return (
    <View style={styles.destinationCard} wrap={false}>
      <Text style={styles.destinationType}>{type}</Text>
      <Text style={styles.destinationName}>{name || "Not specified"}</Text>

      <View style={styles.destinationMetaRow}>
        <View style={styles.destinationMetaBlock}>
          <Text style={styles.destinationMetaLabel}>Address</Text>
          {addressLines.length ? (
            addressLines.map((line, idx) => (
              <Text key={idx} style={styles.destinationMetaText}>
                {line}
              </Text>
            ))
          ) : (
            <Text style={styles.destinationMetaText}>Not provided</Text>
          )}
        </View>

        <View style={styles.destinationMetaBlock}>
          <Text style={styles.destinationMetaLabel}>Contact</Text>
          {contactLines.length ? (
            contactLines.map((item, idx) => (
              <Text
                key={idx}
                style={
                  item.confidential
                    ? styles.confidentialText
                    : styles.destinationMetaText
                }
              >
                {item.text}
              </Text>
            ))
          ) : (
            <Text style={styles.destinationMetaText}>Not provided</Text>
          )}
        </View>
      </View>

      {accessInfo && (
        <View style={styles.destinationAccessBlock}>
          <Text style={styles.destinationMetaLabel}>Access Information</Text>
          <Text style={styles.destinationMetaText}>{accessInfo}</Text>
        </View>
      )}
    </View>
  );
};

const ContactBlock = ({ person }) => {
  if (!person) return <Text style={styles.infoValue}>Not assigned</Text>;

  const fullName = `${person?.userAccount?.firstName ?? ""} ${
    person?.userAccount?.lastName ?? ""
  }`.trim();

  const phone = person?.personnelInfo?.phoneNumber2 || "";

  return (
    <View>
      {!!fullName && <Text style={styles.stackedText}>{fullName}</Text>}
      {!!person?.userAccount?.email && (
        <Text style={styles.stackedText}>
          Email: {person.userAccount.email}
        </Text>
      )}
      {!!phone && <Text style={styles.stackedText}>Tel: {phone}</Text>}
      {!!person?.personnelInfo?.radioCallSign && (
        <Text style={styles.stackedText}>
          {person.personnelInfo.radioCallSign}
        </Text>
      )}
    </View>
  );
};

const OPELsTable = ({ opels = [] }) => {
  if (!opels.length) return <Text style={styles.infoValue}>Not provided</Text>;

  return (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <View style={{ width: "18%" }}>
          <Text style={styles.tableHeaderCell}>Level</Text>
        </View>
        <View style={{ width: "41%" }}>
          <Text style={styles.tableHeaderCell}>Indicators</Text>
        </View>
        <View style={{ width: "41%" }}>
          <Text style={styles.tableHeaderCell}>Actions</Text>
        </View>
      </View>

      {opels.map((opel, index) => (
        <View key={index} style={styles.tableRow}>
          <View style={{ width: "18%" }}>
            <Text style={styles.tableCell}>{opel.level}</Text>
          </View>

          <View style={{ width: "41%" }}>
            {(opel?.indicators || []).map((indicator, idx) => (
              <Text key={idx} style={styles.tableCell}>
                • {indicator}
              </Text>
            ))}
          </View>

          <View style={{ width: "41%" }}>
            {(opel?.actions || []).map((action, idx) => (
              <Text key={idx} style={styles.tableCell}>
                • {action}
              </Text>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
};

const formatTimeRange = (start, end) => {
  if (!start) return "";
  const startM = moment(start);
  const endM = end ? moment(end) : null;
  const startText = startM.format("ddd D MMM HH:mm");
  if (!endM) return startText;
  return startM.isSame(endM, "day")
    ? `${startText} - ${endM.format("HH:mm")}`
    : `${startText} - ${endM.format("ddd D MMM HH:mm")}`;
};

const personName = (person) =>
  `${person?.firstName ?? ""} ${person?.lastName ?? ""}`.trim();

const shiftStaffName = (shift) => {
  if (shift?.assignedSubcontractor) {
    const name = personName(shift.assignedSubcontractor.userAccount);
    return `${name || "Subcontractor"} (Subcontractor)`;
  }
  if (shift?.assignedTo) {
    return personName(shift.assignedTo.userAccount) || "Assigned";
  }
  if (shift?.assignedToSubcontractor) return "Subcontractor";
  return "Vacant";
};

const DataTable = ({ columns, rows }) => (
  <View style={styles.table}>
    <View style={styles.tableHeader}>
      {columns.map((column) => (
        <View key={column.key} style={{ width: column.width }}>
          <Text style={styles.tableHeaderCell}>{column.label}</Text>
        </View>
      ))}
    </View>

    {rows.map((row, index) => (
      <View key={index} style={styles.tableRow} wrap={false}>
        {columns.map((column) => (
          <View key={column.key} style={{ width: column.width }}>
            <Text style={styles.tableCell}>{row[column.key] || "-"}</Text>
          </View>
        ))}
      </View>
    ))}
  </View>
);

const ShiftsTable = ({ shifts = [] }) => (
  <DataTable
    columns={[
      { key: "time", label: "Time", width: "34%" },
      { key: "role", label: "Role", width: "18%" },
      { key: "assignment", label: "Assignment", width: "20%" },
      { key: "staff", label: "Staff", width: "28%" },
    ]}
    rows={shifts.map((shift) => ({
      time: `${formatTimeRange(shift.start, shift.end)}${
        shift.isOnCall ? " (On call)" : ""
      }`,
      role: shift.clinicalRole,
      assignment: shift.eventAssignment,
      staff: shiftStaffName(shift),
    }))}
  />
);

const VehiclesTable = ({ vehicles = [] }) => (
  <DataTable
    columns={[
      { key: "callSign", label: "Call Sign", width: "17%" },
      { key: "reg", label: "Registration", width: "16%" },
      { key: "type", label: "Type", width: "17%" },
      { key: "time", label: "Deployed", width: "30%" },
      { key: "drivers", label: "Drivers", width: "20%" },
    ]}
    rows={vehicles.map((deployment) => ({
      callSign: deployment.vehicle?.callSign,
      reg: deployment.vehicle?.regPlate,
      type: [
        deployment.vehicle?.vehicleInfo?.make,
        deployment.vehicle?.vehicleType,
      ]
        .filter(Boolean)
        .join(" "),
      time: formatTimeRange(deployment.start, deployment.end),
      drivers: [
        deployment.driverTo && `To: ${personName(deployment.driverTo)}`,
        deployment.driverFrom && `From: ${personName(deployment.driverFrom)}`,
      ]
        .filter(Boolean)
        .join("\n"),
    }))}
  />
);

const hasMeaningfulValue = (value) => {
  if (value == null) return false;

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const hasSectionContent = (rows = []) =>
  rows.some((row) => hasMeaningfulValue(row.value));

const EMPDocument = ({
  user,
  eventDoc,
  orgSettings,
  includeShifts = false,
  includeVehicles = false,
}) => {
  const operationalRows = [
    {
      title: "Event Start Date",
      value: moment(eventDoc.startDate).format("Do MMM YYYY HH:mm"),
    },
    {
      title: "Event End Date",
      value: moment(eventDoc.endDate).format("Do MMM YYYY HH:mm"),
    },
    { title: "Description", value: eventDoc?.opsPlan?.description },
    {
      title: "Personnel",
      value: eventDoc?.opsPlan?.personnelSummary,
    },
    {
      title: "Resources",
      value: eventDoc?.opsPlan?.resourcesSummary,
    },
    { title: "Location", value: eventDoc?.opsPlan?.location },
    { title: "What3Words", value: eventDoc?.opsPlan?.what3Words },
    {
      title: "Event Organiser Contact",
      value: eventDoc?.opsPlan?.clientContact,
    },
    {
      title: "Onsite Team Leader",
      value: <ContactBlock person={eventDoc?.opsPlan?.onsiteTeamLeader} />,
    },
    {
      title: "Operational Manager for Event",
      value: <ContactBlock person={eventDoc?.opsPlan?.opsManager} />,
    },
    {
      title: "Clinical Lead for Event",
      value: <ContactBlock person={eventDoc?.opsPlan?.clinicalLead} />,
    },
    {
      title: "Vehicle Movements",
      value: eventDoc?.opsPlan?.vehicleMovements,
    },
    { title: "Site Build", value: eventDoc?.opsPlan?.siteBuild },
    {
      title: "Staff Parking",
      value: eventDoc?.opsPlan?.staffParking,
    },
    {
      title: "Staff Welfare",
      value: eventDoc?.opsPlan?.staffWelfare,
    },
  ];

  const destinationsValue = (
    <View>
      {[
        { key: "nearestED", label: "Emergency Department" },
        { key: "nearestMIU", label: "Minor Injury Unit" },
        { key: "nearestMTC", label: "Major Trauma Centre" },
        { key: "nearestPPCI", label: "PPCI" },
        { key: "nearestTraumaUnit", label: "Trauma Unit" },
        { key: "nearestStroke", label: "Stroke Centre" },
        { key: "nearestPaediatrics", label: "Paediatrics" },
        {
          key: "nearestPaediatricMTC",
          label: "Paediatric MTC",
        },
      ].map(({ key, label }) => {
        const destination = eventDoc?.medPlan?.[key];
        if (!destination) return null;

        const addressLines = [
          typeof destination.address === "string" ? destination.address : null,
          typeof destination.what3words === "string"
            ? `///${destination.what3words}`
            : null,
        ].filter(Boolean);

        const contactLines = [
          typeof destination.publicPhone === "string"
            ? { text: destination.publicPhone }
            : null,
          typeof destination.privatePhone === "string"
            ? {
                text: `${destination.privatePhone} (CONFIDENTIAL)`,
                confidential: true,
              }
            : null,
        ].filter(Boolean);

        return (
          <View minPresenceAhead={120} key={key}>
            <Destination
              type={label}
              name={destination.name}
              addressLines={addressLines}
              contactLines={contactLines}
              accessInfo={destination?.accessInfo}
            />
          </View>
        );
      })}
    </View>
  );

  const hasDestinations = [
    "nearestED",
    "nearestMIU",
    "nearestMTC",
    "nearestPPCI",
    "nearestTraumaUnit",
    "nearestStroke",
    "nearestPaediatrics",
    "nearestPaediatricMTC",
  ].some((key) => !!eventDoc?.medPlan?.[key]);

  const clinicalGovernanceRows = [
    {
      title: "Command & Communications",
      value: eventDoc?.medPlan?.commandCommunications,
    },
    {
      title: "Clinical Records",
      value: eventDoc?.medPlan?.patientReportForms,
    },
    {
      title: "Public Health Considerations",
      value: eventDoc?.medPlan?.publicHealth,
    },
    {
      title: "Safeguarding Considerations",
      value: eventDoc?.medPlan?.safeguarding,
    },
    {
      title: "Special Considerations",
      value: eventDoc?.medPlan?.specialNotes,
    },
    {
      title: "Infection Prevention & Control",
      value: eventDoc?.medPlan?.ipcConsiderations,
    },
  ];

  const transportRows = [
    {
      title: "Emergency Vehicle Access",
      value: eventDoc?.medPlan?.emergencyVehicleAccess,
    },
    {
      title: "Hospital Transport",
      value: eventDoc?.medPlan?.hospitalTransport,
    },
    {
      title: "Destinations",
      value: hasDestinations ? destinationsValue : null,
    },
    {
      title: "Major Trauma Network",
      value: eventDoc?.medPlan?.majorTraumaNetwork,
    },
  ];

  const martynsLawRows = [
    {
      title: "Martyn's Law Tier",
      value: eventDoc?.medPlan?.martynsLawTier,
    },
    {
      title: "Responsible Person / Organisation",
      value: eventDoc?.medPlan?.martynsLawResponsiblePerson,
    },
    {
      title: "Senior Individual",
      value: eventDoc?.medPlan?.martynsLawSeniorIndividual,
    },
    {
      title: "Attendance Assessment Basis",
      value: eventDoc?.medPlan?.martynsLawAttendanceBasis,
    },
    {
      title: "SIA Notification / Evidence",
      value: eventDoc?.medPlan?.martynsLawSiaNotification,
    },
    {
      title: "Evacuation Procedure",
      value: eventDoc?.medPlan?.martynsLawEvacuation,
    },
    {
      title: "Invacuation / Shelter Procedure",
      value: eventDoc?.medPlan?.martynsLawInvacuation,
    },
    {
      title: "Lockdown Procedure",
      value: eventDoc?.medPlan?.martynsLawLockdown,
    },
    {
      title: "Public Alerting & Communication",
      value: eventDoc?.medPlan?.martynsLawPublicComms,
    },
    {
      title: "Protective Security Measures",
      value: eventDoc?.medPlan?.martynsLawProtectiveMeasures,
    },
    {
      title: "Search, Screening & Hostile Vehicle Mitigation",
      value: eventDoc?.medPlan?.martynsLawScreeningHvm,
    },
    {
      title: "Security of Sensitive Information",
      value: eventDoc?.medPlan?.martynsLawInformationSecurity,
    },
    {
      title: "Staff Briefing & Training",
      value: eventDoc?.medPlan?.martynsLawStaffBriefing,
    },
    {
      title: "Multi-agency Co-ordination",
      value:
        eventDoc?.medPlan?.martynsLawCoordinationConfirmed === true
          ? "Confirmed"
          : null,
    },
  ];

  const escalationRows = [
    {
      title: "Escalation of Care",
      value: eventDoc?.medPlan?.escalationOfCare,
    },
    {
      title: "Major Incident Plan",
      value: eventDoc?.medPlan?.majorIncidentPlan,
    },
    {
      title: "Operational Overwhelm Plan",
      value: eventDoc?.medPlan?.overwhelmPlan,
    },
    {
      title: "Operational Pressure Escalation Levels (OPELs)",
      value:
        eventDoc?.medPlan?.opels?.length > 0 ? (
          <OPELsTable opels={eventDoc.medPlan.opels} />
        ) : null,
    },
  ];

  const riskRows = [
    {
      title: "Level of Cover",
      value: eventDoc?.riskAssessment?.levelOfMedicalCoverNote,
    },
    {
      title: "Risk Mitigation",
      value: eventDoc?.riskAssessment?.riskMitigationNote,
    },
    {
      title: "High Risk Activities",
      value: eventDoc?.riskAssessment?.highRiskActivities,
    },
    {
      title: "Venue Type",
      value: eventDoc?.riskAssessment?.venueType,
    },
    {
      title: "Number Attending",
      value: eventDoc?.riskAssessment?.numberAttending,
    },
    {
      title: "Alcohol Consumption",
      value: eventDoc?.riskAssessment?.alcoholConsumption,
    },
    {
      title: "Recreational Drugs",
      value: eventDoc?.riskAssessment?.recreationalDrugUse,
    },
    {
      title: "Risk of Injury",
      value: eventDoc?.riskAssessment?.activitiesRiskInjury,
    },
    {
      title: "Hospital Referrals",
      value: eventDoc?.riskAssessment?.hospitalReferrals,
    },
    {
      title: "Overnight Camping",
      value: eventDoc?.riskAssessment?.overnightCamping,
    },
    {
      title: "Previous Data",
      value: eventDoc?.riskAssessment?.prevEventData,
    },
    ...(eventDoc?.riskAssessment?.highRiskActivities?.includes("Equestrian")
      ? [
          {
            title: "Affiliation",
            value: eventDoc?.riskAssessment?.equestrianAffiliation,
          },
          {
            title: "Client Arranging Doctor?",
            value: eventDoc?.riskAssessment?.equestrianDoctorPresent,
          },
          {
            title: "Highest Fence Height",
            value: eventDoc?.riskAssessment?.equestrianXCFenceHeight,
          },
        ]
      : []),
    ...(eventDoc?.riskAssessment?.highRiskActivities?.includes(
      "Motor Racing or Motorsports",
    )
      ? [
          {
            title: "Track Control",
            value: eventDoc?.riskAssessment?.motorsportTrackControlled,
          },
          {
            title: "Fire Marshalls",
            value: eventDoc?.riskAssessment?.motorsportFireMarshalls,
          },
          {
            title: "Extrication Team",
            value: eventDoc?.riskAssessment?.motorsportExtricationTeam,
          },
          {
            title: "Comms with Drivers",
            value: eventDoc?.riskAssessment?.motorsportRadioToDrivers,
          },
        ]
      : []),
  ];

  const mitigationRows = [
    {
      title: "Personnel",
      value: eventDoc?.riskAssessment?.mitigateStaff,
    },
    {
      title: "Comms Failure",
      value: eventDoc?.riskAssessment?.mitigateCommsFailure,
    },
    {
      title: "Major Incidents",
      value: eventDoc?.riskAssessment?.mitigateMajorIncidents,
    },
    {
      title: "Terrorist Attack",
      value: eventDoc?.riskAssessment?.mitigateTerroristAttack,
    },
    {
      title: "Vehicle Failure",
      value: eventDoc?.riskAssessment?.mitigateVehicleFailure,
    },
    {
      title: "Medical Guidelines",
      value: eventDoc?.riskAssessment?.mitigateMedicalGuidelines,
    },
  ];

  const methodRows = [
    {
      title: "Pre-Event Preparation",
      value: eventDoc?.riskAssessment?.methodPreEventPrep,
    },
    {
      title: "Staffing & Training",
      value: eventDoc?.riskAssessment?.methodStaffingTraining,
    },
    {
      title: "Medical Facilities & Equipment",
      value: eventDoc?.riskAssessment?.methodFacilities,
    },
    {
      title: "Medications",
      value: eventDoc?.riskAssessment?.methodMedications,
    },
    {
      title: "Communications",
      value: eventDoc?.riskAssessment?.methodCommunications,
    },
    {
      title: "NHS Impact",
      value: eventDoc?.riskAssessment?.methodNHSImpact,
    },
    {
      title: "Partnership Working",
      value: eventDoc?.riskAssessment?.methodPartnerWorking,
    },
    {
      title: "Escalating Concerns",
      value: eventDoc?.riskAssessment?.methodEscalatingConcerns,
    },
    {
      title: "Patient Confidentiality",
      value: eventDoc?.riskAssessment?.methodConfidentiality,
    },
    {
      title: "Post-Event Analysis",
      value: eventDoc?.riskAssessment?.methodPostEventAnalysis,
    },
    {
      title: "Event Provider Feedback",
      value: eventDoc?.riskAssessment?.methodFeedback,
    },
  ];

  const sections = [
    {
      headerName: "Operational Plan",
      color: COLORS.navy,
      rows: operationalRows,
      alwaysBreak: false,
    },
    ...(includeShifts
      ? [
          {
            headerName: "Shifts",
            color: COLORS.navy,
            rows: [
              {
                title: `${eventDoc?.shifts?.length ?? 0} shift${
                  eventDoc?.shifts?.length === 1 ? "" : "s"
                }`,
                value: eventDoc?.shifts?.length ? (
                  <ShiftsTable shifts={eventDoc.shifts} />
                ) : (
                  "No shifts have been added to this event."
                ),
              },
            ],
            alwaysBreak: true,
          },
        ]
      : []),
    ...(includeVehicles
      ? [
          {
            headerName: "Vehicles",
            color: COLORS.navy,
            rows: [
              {
                title: `${eventDoc?.vehicles?.length ?? 0} vehicle${
                  eventDoc?.vehicles?.length === 1 ? "" : "s"
                }`,
                value: eventDoc?.vehicles?.length ? (
                  <VehiclesTable vehicles={eventDoc.vehicles} />
                ) : (
                  "No vehicles have been booked onto this event."
                ),
              },
            ],
            alwaysBreak: true,
          },
        ]
      : []),
    {
      headerName: "Clinical Considerations",
      color: COLORS.red,
      rows: clinicalGovernanceRows,
      alwaysBreak: true,
    },
    {
      headerName: "Access, Transport & Receiving Hospitals",
      color: COLORS.blue,
      rows: transportRows,
      alwaysBreak: true,
    },
    {
      headerName: "Public Protection & Martyn's Law",
      color: COLORS.purple,
      rows: martynsLawRows,
      alwaysBreak: true,
    },
    {
      headerName: "Escalation, Overwhelm & Major Incidents",
      color: COLORS.red,
      rows: escalationRows,
      alwaysBreak: true,
    },
    {
      headerName: "Risk Assessment",
      color: COLORS.green,
      rows: riskRows,
      alwaysBreak: true,
    },
    {
      headerName: "Medical Plan Mitigations",
      color: COLORS.purple,
      rows: mitigationRows,
      alwaysBreak: true,
    },
    {
      headerName: "Method Statements",
      color: COLORS.orange,
      rows: methodRows,
      alwaysBreak: true,
    },
  ].filter((section) => hasSectionContent(section.rows));

  return (
    <Document options={{ isEvalSupported: false }}>
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverWrap}>
          <Text style={styles.coverKicker}>Event Medical Plan</Text>

          <Image src={orgSettings.logo} style={styles.coverLogo} />

          <Text style={styles.coverEventTitle}>{eventDoc.title}</Text>

          {eventDoc?.eventLocation && (
            <Text style={styles.coverMeta}>{eventDoc.eventLocation}</Text>
          )}

          <Text style={styles.coverMeta}>
            {moment(eventDoc.startDate).format("Do MMM YYYY")}
            {moment(eventDoc.startDate).startOf("day").unix() !==
              moment(eventDoc.endDate).startOf("day").unix() &&
              moment(eventDoc.endDate).format(" - Do MMM YYYY")}
          </Text>

          <View style={styles.coverCard}>
            <Text style={styles.coverCardTitle}>Issued by</Text>
            <Text style={styles.infoValue}>{orgSettings.orgName}</Text>
            {!!orgSettings.address?.line1 && (
              <Text style={styles.infoValue}>{orgSettings.address.line1}</Text>
            )}
            {!!orgSettings.address?.line2 && (
              <Text style={styles.infoValue}>{orgSettings.address.line2}</Text>
            )}
            {!!orgSettings.address?.town && (
              <Text style={styles.infoValue}>{orgSettings.address.town}</Text>
            )}
            {!!orgSettings.address?.county && (
              <Text style={styles.infoValue}>{orgSettings.address.county}</Text>
            )}
            {!!orgSettings.address?.postCode && (
              <Text style={styles.infoValue}>
                {orgSettings.address.postCode}
              </Text>
            )}
            {!!orgSettings?.publicWebsite && (
              <Text
                style={[styles.infoValue, { marginTop: 8, color: COLORS.navy }]}
              >
                {orgSettings.publicWebsite}
              </Text>
            )}
          </View>
        </View>

        <View
          fixed
          style={{
            position: "absolute",
            bottom: 24,
            left: 32,
            right: 32,
          }}
        >
          {!eventDoc.EMPApproved && (
            <View style={styles.draftBanner}>
              <Text style={styles.draftBannerText}>
                DRAFT - NOT FOR CIRCULATION
              </Text>
            </View>
          )}

          <SectionCard
            color={COLORS.blue}
            headerName="Document Information"
            rows={[
              {
                title: "Status",
                value: eventDoc?.EMPApproved
                  ? `Approved by ${eventDoc?.EMPApprovedBy?.firstName} ${
                      eventDoc?.EMPApprovedBy?.lastName
                    } on ${moment(eventDoc?.EMPApprovedDate).format(
                      "Do MMM YYYY [at] HH:mm",
                    )}`
                  : "Draft",
              },
              {
                title: "Printed By",
                value: [
                  `${user?.firstName} ${user?.lastName}`,
                  moment().format("Do MMM YYYY"),
                  "Please ensure you're viewing the most up to date version.",
                ],
              },
            ]}
          />
        </View>
      </Page>

      <Page size="A4" style={styles.page}>
        <View fixed style={styles.topBar}>
          <Image src={orgSettings.logo} style={styles.topBarLogo} />
          <Text style={styles.topBarOrg}>{orgSettings.orgName}</Text>
        </View>

        <Text style={styles.bodyTitle}>Event Medical Plan</Text>
        <Text style={styles.bodySubtitle}>{eventDoc?.title}</Text>

        {!eventDoc.EMPApproved && (
          <View style={styles.draftBanner}>
            <Text style={styles.draftBannerText}>
              DRAFT - NOT FOR CIRCULATION
            </Text>
          </View>
        )}

        {sections.map((section, index) => {
          const shouldBreak =
            index > 0 && (section.alwaysBreak || isSectionLong(section.rows));

          return (
            <View key={section.headerName} break={shouldBreak}>
              <SectionCard
                headerName={section.headerName}
                color={section.color}
                rows={section.rows}
              />
            </View>
          );
        })}

        <Text
          style={styles.footerLeft}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
        <Text style={styles.footerRight} fixed>
          {eventDoc.EMPApproved
            ? `Event Medical Plan | ${eventDoc?.title}`
            : "DRAFT - NOT FOR CIRCULATION"}
        </Text>
      </Page>
    </Document>
  );
};

const EMP = ({ eventID, saveCopy, user, orgSettings }) => {
  const [generatePDF, setGeneratePDF] = useState(false);
  const [event, setEvent] = useState({});
  const [includeShifts, setIncludeShifts] = useState(false);
  const [includeVehicles, setIncludeVehicles] = useState(false);

  // Changing what goes in means the rendered PDF is stale
  const toggleOption = (setter) => (e) => {
    setter(e.target.checked);
    setGeneratePDF(false);
  };

  const { eventMeta } = useContext(EventMetaContext);

  useEffect(() => {
    const id = eventID || eventMeta?._id;
    if (!id) return;
    API.get("/event-emp/" + id).then((res) => {
      if (res.status === 200) {
        setEvent(res.data);
      }
    });
  }, [eventID, eventMeta?._id]);

  return (
    <Stack>
      <FormGroup row sx={{ mb: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeShifts}
              onChange={toggleOption(setIncludeShifts)}
            />
          }
          label="Include shifts"
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includeVehicles}
              onChange={toggleOption(setIncludeVehicles)}
            />
          }
          label="Include vehicles"
        />
      </FormGroup>

      {!generatePDF && (
        <Button
          variant="contained"
          size="small"
          color="anMainBlue"
          fullWidth
          onClick={() => setGeneratePDF(true)}
        >
          Generate Medical Plan
        </Button>
      )}

      {generatePDF && (
        <PDFDownloadLink
          document={
            <EMPDocument
              user={user}
              eventDoc={event}
              orgSettings={orgSettings}
              includeShifts={includeShifts}
              includeVehicles={includeVehicles}
            />
          }
          fileName={() =>
            `EMP_${(event.title ?? "Event").replace(/\s/g, "_")}_${moment(
              event.startDate,
            )
              .format("YYYYMMDD")
              .toString()}`
          }
        >
          {({ blob, url, loading, error }) => (
            <Button
              variant="contained"
              disabled={loading}
              size="small"
              color="anMainBlue"
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                if (url) {
                  window.open(url, "_blank", "noreferrer");
                }
              }}
            >
              {loading ? "Loading document..." : "Download Medical Plan"}
            </Button>
          )}
        </PDFDownloadLink>
      )}
    </Stack>
  );
};

export default EMP;
